// Global
import React from 'react';

// Local
import styles from './ListViewRow.module.scss';
import { SearchRecord } from 'lib/services/models/search-response';
import { ContentOverride, CardFields } from '../CardListingShared';
import { ContentGridContentResultAnalyticsConfig } from 'components/helpers/Analytics/ContentGrid/ContentGridAnalytics';
import { SearchAnalyticsType } from 'components/helpers/Analytics/SearchAndFilters/SearchAndFiltersAnalytics';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { useMetaDataTag } from 'lib/hooks/useMetaDataTag';
import { Title, Description, useContentResultClickHandler } from './ContentGridCard';

export interface ListViewRowProps {
  card: SearchRecord;
  contentOverride?: ContentOverride;
  fieldsToShow: CardFields[];
  searchType?: SearchAnalyticsType;
  searchAnalyticsEnabled?: boolean;
  contentResultAnalytics?: ContentGridContentResultAnalyticsConfig;
}

// Text-only alternative to ContentGridCard used when the CardView rendering
// parameter is set to "List View": a bold underlined title plus description,
// with no image, eyebrow, rating, or favorites affordances (per Figma spec).
export const ListViewRow = (props: ListViewRowProps) => {
  const { card, contentOverride, fieldsToShow } = props;
  const { locale } = useGlobalData();
  const { sitecoreContext } = useSitecoreContext();
  const { labelName: metaDataTagLabelName } = useMetaDataTag(card.MetadataIds);
  const hideLink = !!contentOverride?.hideLink;

  const handleSearchResultClick = useContentResultClickHandler({
    card,
    contentOverride,
    searchAnalyticsEnabled: props.searchAnalyticsEnabled,
    searchType: props.searchType,
    contentResultAnalytics: props.contentResultAnalytics,
  });

  return (
    <div className={styles.listViewRow}>
      <Title
        fieldsToShow={fieldsToShow}
        url={contentOverride?.url || card.ItemUrl}
        title={contentOverride?.title || card.Title}
        contentType={card.ContentType}
        locale={sitecoreContext?.language || locale}
        tag={
          fieldsToShow.includes('ImageLabel')
            ? contentOverride?.tag || metaDataTagLabelName
            : undefined
        }
        onClick={handleSearchResultClick}
        disableLink={hideLink}
        releaseDate={contentOverride?.releaseDate}
        variant="list"
      />
      <Description
        fieldsToShow={fieldsToShow}
        description={contentOverride?.description || card.Description || ''}
        variant="list"
      />
    </div>
  );
};

export default ListViewRow;
